import React, { useEffect, useState } from 'react'
import * as FiIcons from 'react-icons/fi'
import supabase from '../../lib/supabase'

const { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiTag } = FiIcons

/**
 * CategoriesManager
 *
 * Canonical source of categories: public.codevaro_categories
 *
 * Notes:
 * - codevaro_services.category (text) is legacy and must NOT be used.
 * - Categories are linked via codevaro_services.category_id (primary) and
 *   public.codevaro_service_categories (multi-category memberships).
 * - Live DB schema does NOT include display_order on codevaro_categories.
 */
export default function CategoriesManager() {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const emptyForm = {
    name: '',
    slug: '',
    description: '',
    icon: 'FiTag',
    is_active: true
  }

  const [form, setForm] = useState(emptyForm)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error } = await supabase
        .from('codevaro_categories')
        .select('*')
        .order('name', { ascending: true })

      if (error) throw error
      setCategories(data || [])
    } catch (e) {
      console.error('Error fetching categories:', e)
      setError('Αποτυχία φόρτωσης κατηγοριών.')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setForm(emptyForm)
    setIsAdding(false)
    setEditingId(null)
  }

  const startAdd = () => {
    setIsAdding(true)
    setEditingId(null)
    setForm(emptyForm)
  }

  const startEdit = (cat) => {
    setEditingId(cat.id)
    setIsAdding(false)
    setForm({
      name: cat.name || '',
      slug: cat.slug || '',
      description: cat.description || '',
      icon: cat.icon || 'FiTag',
      is_active: cat.is_active ?? true
    })
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSave = async () => {
    try {
      setError(null)

      if (!form.name.trim()) {
        setError('Το όνομα κατηγορίας είναι υποχρεωτικό.')
        return
      }

      const payload = {
        name: form.name.trim(),
        slug: form.slug?.trim() || null,
        description: form.description?.trim() || null,
        icon: form.icon?.trim() || null,
        is_active: !!form.is_active,
        updated_at: new Date().toISOString()
      }

      if (isAdding) {
        const { error } = await supabase.from('codevaro_categories').insert(payload)
        if (error) throw error
      } else if (editingId) {
        const { error } = await supabase
          .from('codevaro_categories')
          .update(payload)
          .eq('id', editingId)
        if (error) throw error
      }

      await fetchCategories()
      resetForm()
    } catch (e) {
      console.error('Error saving category:', e)
      setError(e?.message || 'Αποτυχία αποθήκευσης κατηγορίας.')
    }
  }

  const handleDelete = async (id) => {
    try {
      setError(null)

      const { error } = await supabase
        .from('codevaro_categories')
        .delete()
        .eq('id', id)

      if (error) throw error

      await fetchCategories()
      if (editingId === id) resetForm()
    } catch (e) {
      console.error('Error deleting category:', e)
      setError(e?.message || 'Αποτυχία διαγραφής κατηγορίας.')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Κατηγορίες</h2>
          <p className="text-sm text-gray-600">
            Διαχείριση κατηγοριών υπηρεσιών (πίνακας:{' '}
            <span className="font-mono">codevaro_categories</span>).
          </p>
        </div>

        {!isAdding && !editingId && (
          <button
            onClick={startAdd}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            <FiPlus className="mr-2" /> Νέα Κατηγορία
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {(isAdding || editingId) && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Όνομα *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="π.χ. EPSILON"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input
                name="slug"
                value={form.slug}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="π.χ. epsilon"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Περιγραφή</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={3}
                placeholder="Σύντομη περιγραφή κατηγορίας"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icon (react-icons key)
              </label>
              <input
                name="icon"
                value={form.icon}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="FiPackage"
              />
            </div>

            <div className="flex items-center">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={!!form.is_active}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                Ενεργή
              </label>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={handleSave}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
            >
              <FiSave className="mr-2" /> Αποθήκευση
            </button>
            <button
              onClick={resetForm}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200"
            >
              <FiX className="mr-2" /> Ακύρωση
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {loading ? 'Φόρτωση...' : `${categories.length} κατηγορίες`}
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-6 text-gray-600">Φόρτωση κατηγοριών...</div>
          ) : categories.length === 0 ? (
            <div className="p-6 text-gray-600">Δεν υπάρχουν κατηγορίες.</div>
          ) : (
            categories.map((cat) => (
              <div key={cat.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100">
                      <FiTag className="text-gray-600" />
                    </span>
                    {cat.name}
                    {!cat.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        ανενεργή
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-mono">{cat.slug || '—'}</span>
                    {cat.description ? ` · ${cat.description}` : ''}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => startEdit(cat)}
                    className="inline-flex items-center px-3 py-2 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200"
                  >
                    <FiEdit2 className="mr-2" /> Επεξεργασία
                  </button>
                  <button
                    onClick={() => handleDelete(cat.id)}
                    className="inline-flex items-center px-3 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
                  >
                    <FiTrash2 className="mr-2" /> Διαγραφή
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}